import { useEffect, useState } from "react";
import { Search, CreditCard, Wrench, TrendingDown, Users, Settings, X, Loader2, Sparkles, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const TABS = [
  { key: 'targa',   icon: Search,        label: 'Targa Lookup',  color: 'sky' },
  { key: 'credit',  icon: CreditCard,    label: 'Credit Score',  color: 'emerald' },
  { key: 'tempari', icon: Wrench,        label: 'Tempari Officina', color: 'amber' },
  { key: 'value',   icon: TrendingDown,  label: 'Valutazione',   color: 'fuchsia' },
  { key: 'leads',   icon: Users,         label: 'Lead B2B',      color: 'rose' },
];

export default function Integrations() {
  const [tab, setTab] = useState('targa');
  const [config, setConfig] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Form fields per ogni tab
  const [targaInput, setTargaInput] = useState("");
  const [pivaInput, setPivaInput] = useState("");
  const [tempInputs, setTempInputs] = useState({ marca: '', modello: '', km: 0, alimentazione: 'benzina' });
  const [valueInputs, setValueInputs] = useState({ marca: '', modello: '', anno: 2020, km: 0 });
  const [leadsInputs, setLeadsInputs] = useState({ regione: 'Emilia-Romagna', ateco: '' });

  const load = async () => {
    const r = await api.get("/integrations/config");
    setConfig(r.data || {});
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      let url = '';
      if (tab === 'targa') {
        if (!targaInput.trim()) { toast.error("Inserisci targa"); setBusy(false); return; }
        url = `/integrations/targa/${encodeURIComponent(targaInput.trim().toUpperCase())}`;
      } else if (tab === 'credit') {
        if (!pivaInput.trim()) { toast.error("Inserisci P.IVA"); setBusy(false); return; }
        url = `/integrations/credit-score/${encodeURIComponent(pivaInput.trim())}`;
      } else if (tab === 'tempari') {
        url = `/integrations/tempari?marca=${encodeURIComponent(tempInputs.marca)}&modello=${encodeURIComponent(tempInputs.modello)}&km=${tempInputs.km}&alimentazione=${tempInputs.alimentazione}`;
      } else if (tab === 'value') {
        url = `/integrations/valuation?marca=${encodeURIComponent(valueInputs.marca)}&modello=${encodeURIComponent(valueInputs.modello)}&anno=${valueInputs.anno}&km=${valueInputs.km}`;
      } else if (tab === 'leads') {
        url = `/integrations/leads?regione=${encodeURIComponent(leadsInputs.regione)}&ateco=${encodeURIComponent(leadsInputs.ateco)}`;
      }
      const r = await api.get(url);
      setResult(r.data);
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const saveConfig = async () => {
    const payload = Object.fromEntries(Object.entries(draft).filter(([_, v]) => v && v.trim()));
    if (Object.keys(payload).length === 0) { toast.info("Nessuna modifica"); return; }
    try {
      await api.put("/integrations/config", payload);
      toast.success("Configurazione salvata ✓");
      setDraft({});
      load();
    } catch (e) { toast.error("Errore: " + e.message); }
  };

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            <Sparkles className="text-[#ff0033]" size={28} />
            Integrazioni Esterne
          </h1>
          <p className="text-sm text-white/45 mt-1">
            Dati ufficiali (Targa · P.IVA · Tempari · Valore · Leads) con fallback AI gratuito
          </p>
        </div>
        <button onClick={() => setShowSettings(true)} data-testid="integrations-settings-btn"
          className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 hover:bg-white/[0.08]">
          <Settings size={14} /> Configurazione API Keys
        </button>
      </div>

      {/* TABS */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {TABS.map((t) => {
          const isActive = tab === t.key;
          const cfgKey = { targa: 'openapi', credit: 'cerved', tempari: 'autodata', value: 'eurotax', leads: 'atoka' }[t.key];
          const isPremium = config[cfgKey]?.configured;
          return (
            <button key={t.key} onClick={() => { setTab(t.key); setResult(null); }} data-testid={`integ-tab-${t.key}`}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap border transition inline-flex items-center gap-2 ${
                isActive ? 'bg-[#ff0033]/15 border-[#ff0033]/50 text-white' : 'bg-white/[0.03] border-white/10 text-white/60 hover:text-white'
              }`}>
              <t.icon size={14} /> {t.label}
              {isPremium ? <span className="text-[9px] uppercase bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded">API</span>
                         : <span className="text-[9px] uppercase bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">AI</span>}
            </button>
          );
        })}
      </div>

      {/* INPUT SECTION */}
      <div className="glass rounded-2xl p-5 space-y-3">
        {tab === 'targa' && (
          <>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Targa</label>
            <div className="flex gap-2">
              <input value={targaInput} onChange={(e) => setTargaInput(e.target.value)} placeholder="AA123BB" data-testid="targa-input"
                className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono uppercase" />
              <button onClick={run} disabled={busy} data-testid="targa-search-btn" className="btn-neon px-5 py-2 rounded-lg text-xs inline-flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Cerca
              </button>
            </div>
            <div className="text-[10px] text-white/40">PRA / Motorizzazione · scadenze bollo, revisione, telaio</div>
          </>
        )}
        {tab === 'credit' && (
          <>
            <label className="text-[10px] uppercase tracking-widest text-white/40">P.IVA (11 cifre)</label>
            <div className="flex gap-2">
              <input value={pivaInput} onChange={(e) => setPivaInput(e.target.value)} placeholder="03859111209" data-testid="piva-input"
                className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              <button onClick={run} disabled={busy} data-testid="credit-search-btn" className="btn-neon px-5 py-2 rounded-lg text-xs inline-flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Score
              </button>
            </div>
            <div className="text-[10px] text-white/40">Rating · semaforo rischio · fido consigliato · pregiudizievoli</div>
          </>
        )}
        {tab === 'tempari' && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Marca</label>
              <input value={tempInputs.marca} onChange={(e) => setTempInputs({ ...tempInputs, marca: e.target.value })} placeholder="Fiat" className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Modello</label>
              <input value={tempInputs.modello} onChange={(e) => setTempInputs({ ...tempInputs, modello: e.target.value })} placeholder="500X" className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Km attuali</label>
              <input type="number" value={tempInputs.km} onChange={(e) => setTempInputs({ ...tempInputs, km: +e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" /></div>
            <button onClick={run} disabled={busy} className="btn-neon py-2 rounded-lg text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />} Tempari
            </button>
          </div>
        )}
        {tab === 'value' && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Marca</label>
              <input value={valueInputs.marca} onChange={(e) => setValueInputs({ ...valueInputs, marca: e.target.value })} placeholder="BMW" className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Modello</label>
              <input value={valueInputs.modello} onChange={(e) => setValueInputs({ ...valueInputs, modello: e.target.value })} placeholder="320d" className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Anno</label>
              <input type="number" value={valueInputs.anno} onChange={(e) => setValueInputs({ ...valueInputs, anno: +e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Km</label>
              <input type="number" value={valueInputs.km} onChange={(e) => setValueInputs({ ...valueInputs, km: +e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" /></div>
            <button onClick={run} disabled={busy} className="btn-neon py-2 rounded-lg text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TrendingDown size={14} />} Valuta
            </button>
          </div>
        )}
        {tab === 'leads' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Regione</label>
              <input value={leadsInputs.regione} onChange={(e) => setLeadsInputs({ ...leadsInputs, regione: e.target.value })} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-white/40">Settore ATECO (opzionale)</label>
              <input value={leadsInputs.ateco} onChange={(e) => setLeadsInputs({ ...leadsInputs, ateco: e.target.value })} placeholder="49.41 trasporto merci" className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" /></div>
            <button onClick={run} disabled={busy} className="btn-neon py-2 rounded-lg text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />} Trova Leads
            </button>
          </div>
        )}
      </div>

      {/* RESULTS */}
      {result && (
        <div className="glass rounded-2xl p-5" data-testid="integrations-result">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-lg">Risultato</div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
              result.source?.includes('premium') ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'
            }`}>
              {result.source?.includes('premium') ? '🟢 API ufficiale' : '🟡 AI fallback'}
            </span>
          </div>
          <pre className="text-xs font-mono text-white/80 bg-black/40 rounded-lg p-4 overflow-auto max-h-[500px] whitespace-pre-wrap leading-relaxed">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}

      {/* SETTINGS MODAL */}
      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShowSettings(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="integrations-settings-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display text-2xl font-bold">API Keys Integrazioni</div>
                <p className="text-xs text-white/45 mt-1">Inserisci una key per attivare l'API ufficiale al posto del fallback AI.</p>
              </div>
              <button onClick={() => setShowSettings(false)} className="text-white/40"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              {[
                { key: 'openapi_key',       label: 'OpenAPI.it (Targa Lookup)',  link: 'https://oauth.openapi.it', cur: config.openapi?.key },
                { key: 'cerved_key',        label: 'Cerved (Credit Score)',       link: 'https://www.cerved.com',   cur: config.cerved?.key },
                { key: 'autodata_app_key',  label: 'Autodata App Key',            link: 'https://autodata-group.com', cur: config.autodata?.app_key },
                { key: 'autodata_app_secret', label: 'Autodata App Secret',       link: 'https://autodata-group.com', cur: config.autodata?.app_secret },
                { key: 'eurotax_key',       label: 'Eurotax (Valutazione)',       link: 'https://www.eurotax.com',  cur: config.eurotax?.key },
                { key: 'atoka_key',         label: 'Atoka (Lead B2B)',            link: 'https://atoka.io',         cur: config.atoka?.key },
              ].map((f) => (
                <div key={f.key}>
                  <label className="text-[10px] uppercase tracking-widest text-white/40 flex items-center justify-between">
                    <span>{f.label}</span>
                    <a href={f.link} target="_blank" rel="noreferrer" className="text-[#ff5577] hover:underline">Ottieni →</a>
                  </label>
                  <input value={draft[f.key] || ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    placeholder={f.cur || 'incolla la chiave'} data-testid={`config-${f.key}`}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-xs font-mono" />
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setShowSettings(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button onClick={saveConfig} data-testid="config-save" className="btn-neon px-6 py-2.5 rounded-full text-sm">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
